<script>
import { useAlert } from 'dashboard/composables';
import TeletelSlaAPI from 'dashboard/api/teletel/sla';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Per-inbox SLA policy assignment (owner decision: assignment is per inbox).
// Reads and writes only through the Teletel endpoints, which stay fail-closed
// server-side while TELETEL_SLA_ENABLED is not explicitly true — in that case
// this section renders nothing and the page-level disabled banner explains.
export default {
  components: {
    NextButton,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDisabled: false,
      hasError: false,
      inboxes: [],
      policies: [],
      assignments: {},
    };
  },
  mounted() {
    this.fetchAssignments();
  },
  methods: {
    async fetchAssignments() {
      this.isLoading = true;
      try {
        const { data } = await TeletelSlaAPI.getAssignments();
        this.inboxes = data.inboxes || [];
        this.policies = data.policies || [];
        this.assignments = { ...(data.assignments || {}) };
        this.isDisabled = false;
        this.hasError = false;
      } catch (error) {
        if (error?.response?.status === 403) {
          this.isDisabled = true;
        } else {
          this.hasError = true;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async saveAssignments() {
      this.isSaving = true;
      try {
        const cleaned = {};
        Object.entries(this.assignments).forEach(([inboxId, policyId]) => {
          if (policyId) {
            cleaned[inboxId] = policyId;
          }
        });
        const { data } = await TeletelSlaAPI.updateAssignments(cleaned);
        this.assignments = { ...(data.assignments || {}) };
        useAlert(this.$t('SLA.TELETEL.ASSIGNMENTS.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('SLA.TELETEL.ASSIGNMENTS.API.ERROR_MESSAGE')
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <section class="contents">
    <div
      v-if="!isDisabled && !hasError && !isLoading"
      class="flex flex-col gap-4 mt-8"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('SLA.TELETEL.ASSIGNMENTS.TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-11">
          {{ $t('SLA.TELETEL.ASSIGNMENTS.DESCRIPTION') }}
        </p>
      </div>
      <p v-if="!inboxes.length" class="text-body-main text-n-slate-11">
        {{ $t('SLA.TELETEL.ASSIGNMENTS.EMPTY') }}
      </p>
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center justify-between gap-4"
        >
          <span class="text-body-main text-n-slate-12 truncate">
            {{ inbox.name }}
          </span>
          <select
            v-model="assignments[String(inbox.id)]"
            class="w-64 mb-0 text-sm"
          >
            <option :value="undefined">
              {{ $t('SLA.TELETEL.ASSIGNMENTS.NO_POLICY_OPTION') }}
            </option>
            <option
              v-for="policy in policies"
              :key="policy.id"
              :value="policy.id"
            >
              {{ policy.name }}
            </option>
          </select>
        </div>
        <div class="flex justify-end">
          <NextButton
            :label="$t('SLA.TELETEL.ASSIGNMENTS.SAVE')"
            size="sm"
            :is-loading="isSaving"
            @click="saveAssignments"
          />
        </div>
      </div>
    </div>
  </section>
</template>
